import { Icon } from '@smartgift/frontend-toolkit';
import merge from 'lodash/merge';
import PropTypes from 'prop-types';
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RECOGNITION_STATUSES } from '@hero/constants';
import { ProductListItemDetail, ShowMore } from '@hero/molecules';
import { useNotification } from '@hero/store/hooks';

import VerticalListItem from './Item';
import * as S from './style';
import { HorizontalProductCardsSkeletons } from './VerticalProductCardSkeleton';

function ProductVerticalList({
  featureName = 'recognition',
  products,
  isFetching,
  onRemoveProduct,
  isProductsRemovable = true,
  updateProducts,
  creationProducts,
  selectedProducts,
  featureStatus,
  isCharityList = false
}) {
  const { showNotification } = useNotification();
  const { t } = useTranslation();
  const [modalProduct, setModalProduct] = useState();
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const productListWithOosProducts = useMemo(() => {
    return creationProducts
      ?.map(creationProduct => {
        const p = products.docs?.find(
          p => p.merchantProductId === creationProduct.merchantProductId
        );

        return {
          ...merge({}, creationProduct, p),
          orderable: !!p,
          skus: p
            ? creationProduct?.skus?.map(sku => {
                const aSku = p?.skus?.find(s => s.sku === sku.code);
                return {
                  ...sku,
                  ...aSku
                };
              })
            : creationProduct?.skus
        };
      })
      .filter(p => !!p?.name);
  }, [products?.docs, creationProducts]);

  const onProductClick = product => {
    const selectedProduct =
      products?.docs.find(p => p.merchantProductId === product.merchantProductId) || product;

    const creationProduct = creationProducts?.find(
      c => c?.merchantProductId === selectedProduct?.merchantProductId
    );

    setModalProduct({
      someSkusUnavailable: product?.skus?.some(sku => !sku?.orderable),
      product: merge({}, creationProduct, selectedProduct),
      isSelected: true,
      productWithSkuSelection: {
        ...selectedProduct,
        skus:
          selectedProduct?.skus?.filter(sku =>
            creationProduct?.skus?.some(s => s?.code === sku?.sku)
          ) ?? selectedProduct?.skus
      },
      meta: creationProduct?.meta
    });

    setIsDetailModalOpen(true);
  };

  const showPrompt = (merchantProductId, product) => {
    showNotification({
      title: t('removeItemPromptQuestion', { featureName: featureName }),
      notificationImage: <Icon name="error" size="xxl" color="var(--color-error-default)" />,
      onSubmit: () => onRemoveProduct(merchantProductId, product),
      primaryButtonProps: { color: 'error', children: t('removeItem') },
      secondaryButtonProps: { children: t('keepItem') }
    });
  };

  const handleCloseModal = () => {
    setIsDetailModalOpen(false);
    setModalProduct({});
  };

  const onUpdateProduct = product => {
    updateProducts(
      selectedProducts?.map(item => {
        if (item.merchantProductId === product.merchantProductId) {
          return product;
        }
        return item;
      })
    );
  };

  return (
    <S.ProductVerticalList>
      {isFetching ? (
        <S.ListContainerLoading>
          <HorizontalProductCardsSkeletons count={3} width={360} height={100} />
        </S.ListContainerLoading>
      ) : (
        <S.ListContainer>
          <ShowMore numberOfLines={6} type="array" containerClassName="product-see-more-container">
            {productListWithOosProducts?.map(product => {
              const isAllSkusAvailable =
                !!product?.orderable && product.skus?.some(sku => !!sku.images);

              return (
                <VerticalListItem
                  product={product}
                  {...(isAllSkusAvailable &&
                    !isCharityList && { onClick: () => onProductClick(product) })}
                  key={product?.merchantProductId}
                  {...(isProductsRemovable && {
                    onRemoveItem: () => showPrompt(product.merchantProductId, product)
                  })}
                  isSelected
                  isAvailableSku={isCharityList || product.skus?.some(sku => !sku?.orderable)}
                  isAllSkusAvailable={isCharityList || isAllSkusAvailable}
                />
              );
            })}
          </ShowMore>
        </S.ListContainer>
      )}
      {isDetailModalOpen && (
        <ProductListItemDetail
          isOpen={isDetailModalOpen}
          onRequestClose={handleCloseModal}
          onOk={({ merchantProductId }) => {
            onRemoveProduct(merchantProductId);
            handleCloseModal();
          }}
          okText={t('includeThisGift')}
          product={modalProduct.product}
          isSelectedProduct={modalProduct?.isSelected}
          initialSelectedSkus={modalProduct?.productWithSkuSelection?.skus}
          onUpdateProduct={onUpdateProduct}
          isProductsRemovable={isProductsRemovable}
          isDraft={featureStatus === RECOGNITION_STATUSES.draft}
          featureType={featureName}
        />
      )}
    </S.ProductVerticalList>
  );
}

ProductVerticalList.propTypes = {
  featureName: PropTypes.string,
  products: PropTypes.shape({
    docs: PropTypes.array
  }),
  isFetching: PropTypes.bool,
  onRemoveProduct: PropTypes.func,
  isProductsRemovable: PropTypes.bool,
  updateProducts: PropTypes.func,
  creationProducts: PropTypes.array,
  selectedProducts: PropTypes.array,
  featureStatus: PropTypes.string
};

export { ProductVerticalList };
